import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';

/**
 * 待办元数据类型
 * 用于存储广告信息、子任务、提醒等扩展数据
 */
export interface TodoMetadata {
  // 广告相关
  isAd?: boolean;
  adPartner?: string;
  adUrl?: string;
  adCampaign?: string;
  cta?: string; // Call-to-Action 按钮文案
  commission?: number;
  
  // 地区相关
  isRegionSpecific?: boolean;
  regions?: string[];
  
  // 协作相关
  isCollaborative?: boolean;
  assignedMembers?: string[];
  estimatedCost?: number;
  currency?: string;
  
  // 提醒相关
  hasReminder?: boolean;
  reminderDays?: number[];
  
  // 子任务
  hasSubtasks?: boolean;
  subtasks?: Array<{
    id: string;
    title: string;
    completed: boolean;
  }>;
  
  // 未来扩展: 任意 key-value
  [key: string]: any;
}

export interface GroupTodo {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  category: 'transport' | 'accommodation' | 'activities' | 'food' | 'shopping' | 'documents' | 'general';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  assignedTo?: string;
  groupId: string;
  projectId?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  metadata?: TodoMetadata; // 元数据字段
}

export interface GroupProject {
  id: string;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'completed' | 'archived' | 'cancelled';
  type: 'personal' | 'group';
  groupId?: string;
  createdBy: string;
  members?: string[];
  dueDate?: string;
  progress: number;
  createdAt: string;
  updatedAt: string;
}

interface GroupTodosState {
  projects: GroupProject[];
  todos: GroupTodo[];
  currentProject: GroupProject | null;
  loading: boolean;
  error: string | null;
}

const initialState: GroupTodosState = {
  projects: [],
  todos: [],
  currentProject: null,
  loading: false,
  error: null,
};

// Async thunks for projects
export const fetchGroupProjects = createAsyncThunk(
  'groupTodos/fetchGroupProjects',
  async (groupId: string) => {
    const { data, error } = await supabase
      .from('group_projects')
      .select('*')
      .eq('group_id', groupId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
);

export const createGroupProject = createAsyncThunk(
  'groupTodos/createGroupProject',
  async (projectData: Omit<GroupProject, 'id' | 'createdAt' | 'updatedAt' | 'progress'>) => {
    const { data, error } = await supabase
      .from('group_projects')
      .insert([{
        ...projectData,
        group_id: projectData.groupId,
        created_by: projectData.createdBy,
        due_date: projectData.dueDate,
        progress: 0,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
);

export const updateGroupProject = createAsyncThunk(
  'groupTodos/updateGroupProject',
  async ({ id, updates }: { id: string; updates: Partial<GroupProject> }) => {
    const { data, error } = await supabase
      .from('group_projects')
      .update({
        ...updates,
        group_id: updates.groupId,
        due_date: updates.dueDate,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
);

// Async thunks for todos
export const fetchProjectTodos = createAsyncThunk(
  'groupTodos/fetchProjectTodos',
  async (projectId: string) => {
    const { data, error } = await supabase
      .from('travel_todos')
      .select('*')
      // travel_todos uses travel_id for travel/project association in this schema
      .eq('travel_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
);

export const createGroupTodo = createAsyncThunk(
  'groupTodos/createGroupTodo',
  async (todoData: Omit<GroupTodo, 'id' | 'createdAt' | 'updatedAt'>) => {
    const { data, error } = await supabase
      .from('travel_todos')
      .insert([{
        title: todoData.title,
        description: todoData.description,
        completed: todoData.completed,
        category: todoData.category,
        priority: todoData.priority,
        group_id: todoData.groupId,
        // prefer explicit travelId if provided, fall back to projectId param (back-compat)
        travel_id: (todoData as any).travelId || (todoData as any).projectId || null,
        assigned_to: todoData.assignedTo,
        created_by: todoData.createdBy,
        due_date: todoData.dueDate,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
);

export const updateGroupTodo = createAsyncThunk(
  'groupTodos/updateGroupTodo',
  async ({ id, updates }: { id: string; updates: Partial<GroupTodo> }) => {
    const { data, error } = await supabase
      .from('travel_todos')
      .update({
        title: updates.title,
        description: updates.description,
        completed: updates.completed,
        category: updates.category,
        priority: updates.priority,
        group_id: updates.groupId,
        // use travel_id field used in travel_todos
        travel_id: (updates as any).travelId || (updates as any).projectId || null,
        assigned_to: updates.assignedTo,
        due_date: updates.dueDate,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
);

export const completeGroupTodo = createAsyncThunk(
  'groupTodos/completeGroupTodo',
  async (id: string) => {
    const { data, error } = await supabase
        .from('travel_todos')
      .update({ 
        completed: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
);

export const deleteGroupTodo = createAsyncThunk(
  'groupTodos/deleteGroupTodo',
  async (id: string) => {
    const { error } = await supabase
      .from('travel_todos')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return id;
  }
);

const groupTodosSlice = createSlice({
  name: 'groupTodos',
  initialState,
  reducers: {
    clearGroupTodos: (state) => {
      state.projects = [];
      state.todos = [];
      state.currentProject = null;
    },
    setCurrentProject: (state, action: PayloadAction<GroupProject | null>) => {
      state.currentProject = action.payload;
    },
    clearCurrentProject: (state) => {
      state.currentProject = null;
      state.todos = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchGroupProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
      })
      .addCase(fetchGroupProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '獲取團隊專案失敗';
      })
      // Create project
      .addCase(createGroupProject.fulfilled, (state, action) => {
        state.projects.unshift(action.payload);
      })
      // Update project
      .addCase(updateGroupProject.fulfilled, (state, action) => {
        const index = state.projects.findIndex(project => project.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.currentProject?.id === action.payload.id) {
          state.currentProject = action.payload;
        }
      })
      // Fetch todos
      .addCase(fetchProjectTodos.fulfilled, (state, action) => {
        state.todos = action.payload;
      })
      // Create todo
      .addCase(createGroupTodo.fulfilled, (state, action) => {
        state.todos.unshift(action.payload);
      })
      // Update todo
      .addCase(updateGroupTodo.fulfilled, (state, action) => {
        const index = state.todos.findIndex(todo => todo.id === action.payload.id);
        if (index !== -1) {
          state.todos[index] = action.payload;
        }
      })
      // Complete todo
      .addCase(completeGroupTodo.fulfilled, (state, action) => {
        const index = state.todos.findIndex(todo => todo.id === action.payload.id);
        if (index !== -1) {
          state.todos[index] = action.payload;
        }
      })
      // Delete todo
      .addCase(deleteGroupTodo.fulfilled, (state, action) => {
        state.todos = state.todos.filter(todo => todo.id !== action.payload);
      });
  },
});

export const { clearGroupTodos, setCurrentProject, clearCurrentProject } = groupTodosSlice.actions;
export default groupTodosSlice.reducer;
